import type { LocalizedString } from "@repo/shared/types";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LocalizedInput } from "@/components/ui/localized-input";
import {
  useCollectionRiskScores,
  useCreatePortfolio,
  usePlatforms,
  useUpdatePortfolio,
} from "@/lib/hooks/use-portfolio-collections";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";

import { RiskScoreField } from "./detail/portfolio-weights";
import { PlatformPicker } from "./platform-picker";
import { T } from "./portfolio-dialog.i18n";

type PortfolioDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  portfolioCollectionId: string;
  existingRiskScores: number[];
  portfolio?: {
    id: string;
    nameI18n: LocalizedString;
    descriptionI18n: LocalizedString | null;
    riskScore: number;
    /** QP-226 — explicit platform membership (empty = no platform). */
    platformIds: string[];
  } | null;
};

export function PortfolioDialog({
  open,
  onOpenChange,
  portfolioCollectionId,
  existingRiskScores,
  portfolio,
}: PortfolioDialogProps) {
  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);

  const createPortfolio = useCreatePortfolio();
  const updatePortfolio = useUpdatePortfolio();
  const { riskScores: matrixScores } = useCollectionRiskScores(portfolioCollectionId);
  const { platforms } = usePlatforms(portfolioCollectionId);

  // QP-195 — risk-score uniqueness is per platform now, so every matrix score
  // is selectable; the server rejects an active-visibility overlap with a
  // descriptive error. `existingRiskScores` is kept for the prop contract.
  void existingRiskScores;
  const availableRiskScores = useMemo(() => matrixScores, [matrixScores]);

  const isEditing = !!portfolio;
  const editingPortfolioName = portfolio ? localize(portfolio.nameI18n) : "";
  const isSubmitting = createPortfolio.isPending || updatePortfolio.isPending;
  const [riskScore, setRiskScore] = useState("");
  const [name, setName] = useState("");
  const [nameI18n, setNameI18n] = useState<LocalizedString>({});
  const [description, setDescription] = useState("");
  const [descriptionI18n, setDescriptionI18n] = useState<LocalizedString>({});
  const [platformIds, setPlatformIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setError(null);
      if (portfolio) {
        setRiskScore(portfolio.riskScore.toString());
        const portfolioName = localize(portfolio.nameI18n);
        const portfolioDescription = portfolio.descriptionI18n
          ? localize(portfolio.descriptionI18n)
          : "";
        setName(portfolioName);
        setNameI18n(portfolio.nameI18n || {});
        setDescription(portfolioDescription);
        setDescriptionI18n(portfolio.descriptionI18n || {});
        setPlatformIds(portfolio.platformIds ?? []);
      } else {
        setRiskScore("");
        setName("");
        setNameI18n({});
        setDescription("");
        setDescriptionI18n({});
        setPlatformIds([]);
      }
    }
  }, [open, portfolio, localize]);

  const handleSubmit = () => {
    setError(null);

    // Validate name
    if (!name.trim()) {
      setError(t.portfolioNameRequired);
      return;
    }

    if (!isEditing) {
      if (!riskScore) {
        setError(t.riskScoreRequired);
        return;
      }
      const score = parseInt(riskScore, 10);
      if (Number.isNaN(score) || !availableRiskScores.includes(score)) {
        setError(t.riskScoreInvalid);
        return;
      }
    }

    // Build i18n objects with current language value
    const finalNameI18n: LocalizedString = {
      ...nameI18n,
      [userLanguage]: name,
    };
    const finalDescriptionI18n: LocalizedString | undefined = description
      ? {
          ...descriptionI18n,
          [userLanguage]: description,
        }
      : undefined;

    const callbacks = {
      onSuccess: () => onOpenChange(false),
      onError: (err: Error) => {
        setError(err.message || (isEditing ? t.failedToUpdate : t.failedToCreate));
      },
    };

    if (isEditing && portfolio) {
      updatePortfolio.mutate(
        {
          collectionId: portfolioCollectionId,
          portfolioId: portfolio.id,
          data: { nameI18n: finalNameI18n, descriptionI18n: finalDescriptionI18n, platformIds },
        },
        callbacks,
      );
    } else {
      createPortfolio.mutate(
        {
          collectionId: portfolioCollectionId,
          data: {
            riskScore: parseInt(riskScore, 10),
            nameI18n: finalNameI18n,
            descriptionI18n: finalDescriptionI18n,
            platformIds,
          },
        },
        callbacks,
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? editingPortfolioName
                ? `${t.editPortfolio} (${editingPortfolioName})`
                : t.editPortfolio
              : t.createPortfolio}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? t.editDescription : t.createDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">{error}</div>
          )}

          <RiskScoreField
            value={riskScore}
            onChange={setRiskScore}
            scores={availableRiskScores}
            readOnly={isEditing}
            hint={t.riskScoreHint}
          />

          <LocalizedInput
            id="name"
            label={t.portfolioName}
            value={name}
            i18nValue={nameI18n}
            onChange={setName}
            onI18nChange={setNameI18n}
            supportedLanguages={tenantSupportedLanguages}
            placeholder={t.portfolioNamePlaceholder}
            required
          />

          <LocalizedInput
            id="description"
            label={t.description}
            value={description}
            i18nValue={descriptionI18n}
            onChange={setDescription}
            onI18nChange={setDescriptionI18n}
            supportedLanguages={tenantSupportedLanguages}
            placeholder={t.descriptionPlaceholder}
            multiline
            rows={3}
          />

          {/* QP-195 — platform visibility multi-select (only when the collection has platforms) */}
          <PlatformPicker
            platforms={platforms}
            value={platformIds}
            onChange={setPlatformIds}
            label={t.platforms}
            hint={t.platformsHint}
            idPrefix="portfolio-platform"
            disabled={isSubmitting}
          />
        </div>

        <DialogFooter>
          <DialogCancel disabled={isSubmitting}>{t.cancel}</DialogCancel>
          <Button
            onClick={handleSubmit}
            disabled={!isEditing && availableRiskScores.length === 0}
            loading={isSubmitting}
          >
            {isEditing ? t.save : t.create}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
